import React from "react";
import { motion } from "framer-motion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1566221857770-508d35ee6220?fm=jpg&q=80&w=2000&auto=format&fit=crop";

const TAGS = ["Process Safety", "Risk Analysis", "Mechanical Integrity"];
const READOUTS = [
  { label: "Specialized process safety & engineering services", num: "20", unit: "" },
  { label: "Standard PHA revalidation cycle", num: "5", unit: "yr" },
  { label: "Engagements scoped around compliance", num: "100", unit: "%" },
];
const STRIP = ["OSHA 29 CFR 1910.119", "EPA 40 CFR Part 68", "IEC 61511 / ISA 84", "NFPA 652 / 654", "API RP 752 / 753"];

const FADE_UP = { opacity: 0, y: 18 };
const SHOW = { opacity: 1, y: 0 };
const T1 = { duration: 0.6 };
const T2 = { duration: 0.6, delay: 0.1 };
const T3 = { duration: 0.6, delay: 0.2 };

export const Hero = () => {
  return (
    <>
      <section
        id="top"
        data-testid="hero-section"
        className="relative overflow-hidden text-white"
        style={{
          backgroundImage: `linear-gradient(100deg, rgba(4,10,20,0.94) 0%, rgba(4,16,32,0.88) 38%, rgba(4,20,35,0.72) 62%, rgba(6,30,25,0.55) 100%), url(${HERO_IMG})`,
          backgroundPosition: "center 38%",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="absolute inset-0 blueprint-grid pointer-events-none" data-testid="hero-bg" />

        <div className="relative z-[2] mx-auto max-w-[1180px] px-5 lg:px-8 pt-24 pb-16 grid lg:grid-cols-[1.25fr_0.9fr] gap-11 lg:gap-14 items-end">
          <div>
            <motion.div initial={FADE_UP} animate={SHOW} transition={T1} className="flex flex-wrap items-center gap-2 mb-6">
              {TAGS.map((t) => (
                <span
                  key={t}
                  className="font-body text-[12.5px] font-medium text-[#D7DEE7] border border-white/30 px-3 py-1.5"
                >
                  {t}
                </span>
              ))}
            </motion.div>

            <motion.h1
              initial={FADE_UP}
              animate={SHOW}
              transition={T2}
              className="font-display font-bold text-white leading-[1.05] text-4xl sm:text-5xl lg:text-[58px] max-w-[15ch]"
            >
              Engineering rigor for facilities that can't afford to get safety wrong.
            </motion.h1>

            <motion.p
              initial={FADE_UP}
              animate={SHOW}
              transition={T3}
              className="mt-6 font-body text-[17px] leading-[1.65] text-[#C7D1DC] max-w-[46ch]"
            >
              PSIEC facilitates the hazard studies, compliance programs, and mechanical integrity
              work that keep high-hazard industrial and petrochemical sites running — and
              defensible — across twenty specialized process safety disciplines.
            </motion.p>

            <motion.div initial={FADE_UP} animate={SHOW} transition={T3} className="mt-9 flex flex-wrap gap-3.5">
              <a
                href="#services"
                data-testid="hero-cta-explore"
                className="inline-flex items-center justify-center px-[22px] py-3 text-[14.5px] font-semibold text-white bg-[#0E8A57] hover:bg-[#0F9A62] rounded-[2px] transition-colors"
              >
                Explore Our Services
              </a>
              <a
                href="#contact"
                data-testid="hero-cta-audit"
                className="inline-flex items-center justify-center px-[22px] py-3 text-[14.5px] font-semibold text-white border border-white/35 hover:border-white rounded-[2px] transition-colors"
              >
                Schedule a Technical Audit
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={FADE_UP}
            animate={SHOW}
            transition={T3}
            data-testid="hero-snapshot-panel"
            className="border border-white/[0.18] bg-[#061426]/55 px-[26px] pt-[26px] pb-2"
          >
            <div className="font-body text-xs tracking-[0.04em] text-[#8FA0B3] pb-4 border-b border-white/[0.14] mb-1.5">
              FIRM SNAPSHOT
            </div>
            {READOUTS.map((r) => (
              <div
                key={r.label}
                data-testid={`hero-stat-${r.num}`}
                className="flex justify-between items-baseline py-4 border-b border-white/10 last:border-b-0"
              >
                <span className="font-body text-[12.5px] text-[#8FA0B3] max-w-[13ch] leading-[1.4]">{r.label}</span>
                <span className="font-display text-[30px] font-bold text-white">
                  {r.num}
                  {r.unit && <span className="text-base text-[#0E8A57] ml-0.5">{r.unit}</span>}
                </span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <div data-testid="standards-strip" className="bg-[#04162E] border-y border-white/15 py-5 overflow-hidden">
        <div className="mx-auto max-w-[1180px] px-5 lg:px-8 flex flex-wrap items-center justify-center">
          {STRIP.map((s) => (
            <span
              key={s}
              className="font-body text-[13px] text-[#9FB0C2] px-4 py-1.5 sm:py-0 sm:px-[26px] sm:border-r sm:border-white/[0.14] sm:last:border-r-0"
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </>
  );
};
